import { useCallback, useMemo } from 'react'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems?: number
  pageSize?: number
  loading?: boolean
  onPageChange: (page: number) => void
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  loading = false,
  onPageChange,
  className,
}: PaginationProps) {

  // ---------------------------------------------------------------------------
  // Visibility: decide whether to show pagination at all
  // ---------------------------------------------------------------------------
  const shouldShow = useMemo(() => {
    // If we know total items and page size, use them for a precise check
    if (totalItems !== undefined && pageSize !== undefined) {
      return totalItems > pageSize
    }
    // Fallback to the classic totalPages check
    return totalPages > 1
  }, [totalItems, pageSize, totalPages])

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handlePrevious = () => onPageChange(currentPage - 1)
  const handleNext = () => onPageChange(currentPage + 1)

  // ---------------------------------------------------------------------------
  // Page buttons - responsive: full page numbers on desktop, compact on mobile
  // ---------------------------------------------------------------------------
  const getVisiblePages = useCallback(() => {
    const pages: (number | 'ellipsis')[] = []

    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3, 'ellipsis')
      } else if (currentPage >= totalPages - 1) {
        pages.push('ellipsis', totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push('ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis')
      }
    }

    return pages
  }, [currentPage, totalPages])

  const visiblePages = useMemo(() => getVisiblePages(), [getVisiblePages])

  const buttonStyles = "h-9 text-sm border border-transparent rounded-xl text-foreground hover:bg-card hover:border-sidebar-border hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!shouldShow) {
    return null
  }

  return (
    <nav
      className={cn(
        "shrink-0 bg-component border-t",
        className
      )}
      role="navigation"
      aria-label="Table pagination"
    >
      <div className="flex items-center justify-between px-4 py-2 sm:justify-end">
        {/* Mobile: page indicator text (hidden on sm+) */}
        <span className="text-sm text-muted-foreground sm:hidden" aria-live="polite">
          {currentPage} / {totalPages}
        </span>

        <div className="flex items-center gap-1" role="group" aria-label="Pagination controls">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            disabled={currentPage === 1 || loading}
            className={cn(buttonStyles, "px-4 disabled:opacity-50 disabled:cursor-not-allowed")}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4 sm:mr-0.5" />
            <span className="hidden sm:inline">Previous</span>
          </Button>

          {/* Page number buttons - hidden on mobile, visible on sm+ */}
          <div className="hidden sm:flex items-center gap-1">
            {visiblePages.map((page, index) => (
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-sm text-foreground">
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  variant="ghost"
                  size="sm"
                  onClick={() => onPageChange(page)}
                  disabled={loading}
                  className={cn(
                    buttonStyles,
                    "w-9 p-0",
                    currentPage === page && "bg-card border-sidebar-border shadow-sm"
                  )}
                  aria-label={`Go to page ${page}`}
                  aria-current={currentPage === page ? "page" : undefined}
                >
                  {page}
                </Button>
              )
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={currentPage === totalPages || loading}
            className={cn(buttonStyles, "px-4 disabled:opacity-50 disabled:cursor-not-allowed")}
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4 sm:ml-0.5" />
          </Button>
        </div>
      </div>
    </nav>
  )
}
